import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { CommissioningStore, ValidateEuidResult } from '@store';
import { Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { CodeScannerService, UIService } from '../../../../../../../shared/services';
import { ModalAction } from '../../../commissioning.model';
import { CommissioningService } from '../../../commissioning.service';

@Component({
  selector: 'app-commissioning-code-form',
  templateUrl: './commissioning-code-form.component.html',
  styleUrls: ['./commissioning-code-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningCodeFormComponent implements OnInit, OnDestroy {
  ValidateEuidResult = ValidateEuidResult;

  @Input() summaryView = false;
  @Output() localModalAction = new EventEmitter<ModalAction>();
  @Output() openQrScannerAction = new EventEmitter<void>();

  formGroup: FormGroup<{
    id: FormControl<string>;
    ic: FormControl<string>;
  }>;

  euidValidationResult = null;
  isSafari: boolean;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public codeScannerService: CodeScannerService,
    public commissioningService: CommissioningService,
    private commissioningStore: CommissioningStore,
    private dialogRef: MatDialogRef<CommissioningCodeFormComponent>,
    private uiService: UIService
  ) {}

  ngOnInit(): void {
    this.isSafari = this.uiService.isSafariBrowser();
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');
    this.commissioningStore.currentCode$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        map((code) => code || { id: null, ic: null })
      )
      .subscribe((code) => {
        this.formGroup = new FormGroup({
          id: new FormControl<string>(null, [
            Validators.required,
            this.euidPatternValidator,
            this.euidLengthValidator,
          ]),
          ic: new FormControl<string>(null, [
            Validators.required,
            Validators.pattern('[A-Fa-f0-9 ]+'),
            this.icLengthValidator,
          ]),
        });

        setTimeout(() => {
          // Required to refresh mat-labels
          this.formGroup.get('id').setValue(code.id, { emitEvent: true });
          this.formGroup.get('ic').setValue(code.ic, { emitEvent: true });

          if (this.summaryView) {
            this.formGroup.disable();
          }
        }, 0);
      });

    this.commissioningStore.validateEuidResult$
      .pipe(
        filter((entry) => !!entry),
        take(1)
      )
      .subscribe((result: ValidateEuidResult) => {
        this.euidValidationResult = result;
      });
  }

  navigateBack(): void {
    this.commissioningStore.setCode({
      ic: '',
      id: '',
    });
    this.localModalAction.emit(ModalAction.PreviousStep);
  }

  openQrScanner(): void {
    this.openQrScannerAction.next();
  }

  navigateForward(): void {
    const value = this.formGroup.value;
    const code = {
      id: value.id,
      ic: value.ic,
    };
    this.commissioningStore.setCode(code);
    this.commissioningService.currentRemoteDeviceCode$.next(code);
    this.localModalAction.emit(ModalAction.NextStep);
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private euidLengthValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const EUID_VALID_LENGTH = 16;

    if (control.value) {
      const valueWithoutWhitespaces: string = control.value.replace(/ /g, '').replace(/-/g, '');

      if (valueWithoutWhitespaces.length !== EUID_VALID_LENGTH) {
        return { length: true };
      }
    }

    return null;
  }

  private euidPatternValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value) {
      const valueWithoutWhitespaces: string = control.value.replace(/ /g, '').replace(/-/g, '');

      const regex = new RegExp('^[A-Fa-f0-9-]+$');
      const testPassed = regex.test(valueWithoutWhitespaces);

      if (!testPassed) {
        return { pattern: true };
      }
    }

    return null;
  }

  private icLengthValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const IC_VALID_LENGTH = 36;

    if (control.value) {
      const trimmedValue: string = control.value.replace(/\s/g, '');

      if (trimmedValue.length !== IC_VALID_LENGTH) {
        return { length: true };
      }
    }

    return null;
  }
}
